import { useCallback, useEffect, useRef, useState } from "react";
import * as ml from "../../api/ml";
import { listClasses } from "../../api/projects";
import type { LabelClass } from "../../api/types";
import { WEB_IMAGE_SUFFIXES } from "../../lib/dashboard";
import {
  TEST_BATCH, TEST_MAX_PICTURES, THRESHOLD, anchor, colorFor, geometry, label, pictureSummary, runSummary, shown,
} from "../../lib/modelTest";
import { textOn } from "../../lib/classColors";
import { fromZip, planUpload, toFile } from "../../lib/upload";
import { Dialog } from "../../ui/Dialog";
import { useDialogs } from "../../ui/dialogs";
import { Icon } from "../../ui/icons";

interface Picture { key: number; file: File; url: string; result: ml.TestResult | null; version: number | null }

const isZip = (f: File) => f.name.toLowerCase().endsWith(".zip");
const isPicture = (f: File) => WEB_IMAGE_SUFFIXES.some((x) => f.name.toLowerCase().endsWith(x));

/**
 * A picture with what the model found drawn over it: shapes in the picture's own pixels, labels in the page's. The box
 * takes the picture's proportions, so the drawing lies exactly on it, and is no taller than `maxHeight` (a CSS length).
 */
function Overlaid({ p, classes, threshold, maxHeight }: { p: Picture; classes: LabelClass[]; threshold: number; maxHeight: string }) {
  const r = p.result;
  const w = r?.width || 1, h = r?.height || 1;
  const dets = r && !r.error ? shown(r, threshold) : [];
  const fit = r && !r.error
    ? { aspectRatio: `${w} / ${h}`, width: `min(100%, calc(${maxHeight} * ${(w / h).toFixed(4)}))`, margin: "0 auto" }
    : undefined;
  return (
    <div className="test-pic" style={fit}>
      <img src={p.url} alt={p.file.name} draggable={false} />
      {dets.length > 0 && (
        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
          {dets.map((d, i) => {
            const g = geometry(d), color = colorFor(d, classes);
            return g.kind === "rect"
              ? <rect key={i} x={g.x} y={g.y} width={g.w} height={g.h} stroke={color} />
              : <polygon key={i} points={g.points.map((q) => q.join(",")).join(" ")} stroke={color} />;
          })}
        </svg>
      )}
      {dets.map((d, i) => {
        const [x, y] = anchor(d), color = colorFor(d, classes);
        return (
          <span key={i} className={`test-label${y / h < 0.08 ? " inside" : ""}`} data-testid="test-label"
            style={{ left: `${(x / w) * 100}%`, top: `${(y / h) * 100}%`, background: color, color: textOn(color) }}>{label(d)}</span>
        );
      })}
      {!r && <span className="test-pending"><Icon name="loader" size={14} />Testing…</span>}
    </div>
  );
}

/**
 * Models ▸ Test…: what a version finds on pictures from this computer, drawn over them. Nothing is saved and no credits
 * are charged (backend/app/api/model_test.py). The slider filters what was found without running the model again; a
 * different version's Test… runs the same pictures through that version, to compare.
 */
export function ModelTestPanel({ projectId, version, onClose }: { projectId: number; version: number; onClose: () => void }) {
  const dlg = useDialogs();
  const [pictures, setPictures] = useState<Picture[]>([]);
  const [threshold, setThreshold] = useState(THRESHOLD.initial);
  const [classes, setClasses] = useState<LabelClass[]>([]);
  // Runs in flight: a new version's run can start before the last one's answers stop arriving.
  const [running, setRunning] = useState(0);
  const busy = running > 0;
  const [dragging, setDragging] = useState(false);
  const [enlarged, setEnlarged] = useState<number | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const nextKey = useRef(1);
  const urls = useRef<string[]>([]);
  // Results that come back for a version no longer chosen are dropped.
  const current = useRef(version);
  current.current = version;

  useEffect(() => { listClasses(projectId).then(setClasses).catch(() => setClasses([])); }, [projectId]);
  useEffect(() => () => { for (const u of urls.current) URL.revokeObjectURL(u); }, []);

  /** Send pictures a batch at a time and fill in each one's answer as it comes. */
  const run = useCallback(async (batch: Picture[], v: number) => {
    setRunning((n) => n + 1);
    try {
      for (let i = 0; i < batch.length; i += TEST_BATCH) {
        const part = batch.slice(i, i + TEST_BATCH);
        let answers: ml.TestResult[];
        try {
          answers = (await ml.testModel(projectId, part.map((p) => p.file), v)).results;
        } catch (e) {
          const why = (e as Error).message;
          answers = part.map((p) => ({ file: p.file.name, width: 0, height: 0, detections: [], error: why }));
        }
        if (current.current !== v) return;
        const byKey = new Map(part.map((p, n) => [p.key, answers[n]]));
        setPictures((ps) => ps.map((p) => (byKey.has(p.key) ? { ...p, result: byKey.get(p.key) ?? null, version: v } : p)));
      }
    } finally { setRunning((n) => n - 1); }
  }, [projectId]);

  // Another version chosen: the same pictures, through that one.
  const pics = useRef(pictures);
  pics.current = pictures;
  useEffect(() => {
    const stale = pics.current.filter((p) => p.version !== version);
    if (!stale.length) return;
    setPictures((ps) => ps.map((p) => (p.version !== version ? { ...p, result: null } : p)));
    void run(stale, version);
  }, [version, run]);

  const add = async (files: File[]) => {
    const found: File[] = files.filter(isPicture);
    for (const z of files.filter(isZip)) {
      try {
        const plan = planUpload([await fromZip(z)]);
        for (const p of plan.pictures.slice(0, TEST_MAX_PICTURES)) found.push(await toFile(p));
      } catch (e) { await dlg.alert({ title: `Could not open ${z.name}`, text: (e as Error).message }); }
    }
    if (!found.length) {
      await dlg.alert({ title: "No pictures", text: "Choose JPEG, PNG or WebP pictures, or a .zip of them." });
      return;
    }
    const room = TEST_MAX_PICTURES - pictures.length;
    if (found.length > room) {
      await dlg.alert({ title: "Too many pictures", text: room > 0
        ? `Test up to ${TEST_MAX_PICTURES} pictures at a time. The first ${room} of these are tested; clear the panel to test others.`
        : `Test up to ${TEST_MAX_PICTURES} pictures at a time. Clear the panel to test others.` });
      if (room <= 0) return;
    }
    const added = found.slice(0, room).map((file) => {
      const url = URL.createObjectURL(file);
      urls.current.push(url);
      return { key: nextKey.current++, file, url, result: null, version };
    });
    setPictures((ps) => [...ps, ...added]);
    await run(added, version);
  };

  const clear = () => {
    for (const u of urls.current) URL.revokeObjectURL(u);
    urls.current = [];
    setPictures([]);
  };

  const done = pictures.filter((p) => p.result).map((p) => p.result!);
  const big = pictures.find((p) => p.key === enlarged) ?? null;

  return (
    <section className={`card-section test-panel${dragging ? " dragging" : ""}`} data-testid="model-test"
      onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDragging(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false); }}
      onDrop={(e) => { e.preventDefault(); setDragging(false); void add([...e.dataTransfer.files]); }}>
      <div className="section-head">
        <h2 data-testid="model-test-title">Test v{version}</h2>
        <button className="btn ghost icon-btn" onClick={onClose} title="Close" aria-label="Close" data-testid="model-test-close"><Icon name="x" /></button>
      </div>
      <div className="muted">Drop pictures this model has not seen (not ones it was trained on) and see what it finds on them.
        Nothing is saved to the project, and testing costs no credits.</div>

      <div className="test-controls">
        <button className="btn primary" onClick={() => input.current?.click()} data-testid="model-test-choose"><Icon name="image" />Choose pictures…</button>
        <input ref={input} type="file" multiple hidden accept=".jpg,.jpeg,.png,.webp,.zip" data-testid="input-test"
          onChange={(e) => { const f = [...(e.target.files ?? [])]; e.target.value = ""; if (f.length) void add(f); }} />
        <label className="test-threshold" htmlFor="test-threshold">
          <span>How sure the model must be</span>
          <input id="test-threshold" type="range" min={THRESHOLD.min} max={THRESHOLD.max} step={THRESHOLD.step} value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))} data-testid="test-threshold" />
          <b data-testid="test-threshold-value">{threshold}%</b>
        </label>
        <span className="spacer" />
        {pictures.length > 0 && <button className="btn flat" disabled={busy} onClick={clear} data-testid="model-test-clear">Clear</button>}
      </div>

      {pictures.length === 0 ? (
        <div className="test-empty" data-testid="model-test-empty">
          <Icon name="upload" size={22} />
          <span>Drop test pictures here, or use Choose pictures…</span>
          <span className="muted">JPEG, PNG or WebP, or a .zip of them; up to {TEST_MAX_PICTURES} at a time.</span>
        </div>
      ) : <>
        <div className="muted" data-testid="model-test-summary">
          {busy ? `Testing… ${done.length} of ${pictures.length} done` : runSummary(done, threshold)}
        </div>
        <div className="test-grid">
          {pictures.map((p) => (
            <figure key={p.key} className="test-card" data-testid={`test-card-${p.file.name}`}>
              <button className="test-open" onClick={() => setEnlarged(p.key)} title="Show larger" disabled={!p.result}>
                <Overlaid p={p} classes={classes} threshold={threshold} maxHeight="280px" />
              </button>
              <figcaption>
                <span className="test-name">{p.file.name}</span>
                <span className={p.result?.error ? "error-text" : "muted"} data-testid="test-found">{p.result ? pictureSummary(p.result, threshold) : ""}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </>}
      {dragging && <div className="drop-hint"><Icon name="upload" size={22} />Drop to test v{version}</div>}

      {big && (
        <Dialog title={`${big.file.name} · v${version}`} width={1100} onClose={() => setEnlarged(null)} testId="model-test-large">
          <Overlaid p={big} classes={classes} threshold={threshold} maxHeight="(100vh - 220px)" />
          <div className="muted" style={{ marginTop: 8 }}>{big.result ? pictureSummary(big.result, threshold) : ""}</div>
          <div className="dialog-actions"><button className="btn primary" onClick={() => setEnlarged(null)}>Close</button></div>
        </Dialog>
      )}
    </section>
  );
}
